// ─────────────────────────────────────────────────────────────────
// Glass-i — HRMS Employee History page (Stage HRMS-3).
// Append-only change feed with employee / event-type / date filters.
// Read-only (hrms.employee_history.view).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Select, Space, Table, Tag, Tooltip, message } from 'antd';
import { ReloadOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { hrHistoryApi } from './hrHistoryApi';
import { hrEmployeesApi } from '../employees/hrEmployeesApi';
import { EVENT_TYPE_OPTIONS, eventLabel, eventTagColor, formatSnapshot } from './historyHelpers';
import '../hrms.css';

const PAGE_SIZE = 25;
const { RangePicker } = DatePicker;

export default function EmployeeHistoryPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [range, setRange] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (employeeId) params.employeeId = employeeId;
      if (eventType) params.eventType = eventType;
      if (range && range[0]) params.dateFrom = range[0].format('YYYY-MM-DD');
      if (range && range[1]) params.dateTo = range[1].format('YYYY-MM-DD');
      const out = await hrHistoryApi.list(params);
      setRows((out && out.rows) || []);
      setTotal((out && out.total) || 0);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load history.');
    } finally { setLoading(false); }
  }, [employeeId, eventType, range, page]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    hrEmployeesApi.list({ limit: 1000 })
      .then((out) => setEmployees(((out && out.rows) || []).map((x) => ({
        value: x.id, label: `${x.displayName || x.employeeName} (${x.employeeCode})` }))))
      .catch(() => { /* soft-fail */ });
  }, []);

  const columns = [
    { title: 'When', dataIndex: 'createdAt', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '—' },
    { title: 'Employee', dataIndex: 'employeeName', width: 200,
      render: (v, r) => v ? `${v}${r.employeeCode ? ` (${r.employeeCode})` : ''}` : '—' },
    { title: 'Event', dataIndex: 'eventType', width: 180, render: (v) => <Tag color={eventTagColor(v)}>{eventLabel(v)}</Tag> },
    { title: 'Old', dataIndex: 'oldValue', width: 220, render: (v) => <span className="hr-mono">{formatSnapshot(v)}</span> },
    { title: 'New', dataIndex: 'newValue', width: 220, render: (v) => <span className="hr-mono">{formatSnapshot(v)}</span> },
    { title: 'Source', dataIndex: 'sourceTable', width: 160, render: (v) => v || '—' },
    { title: 'Remarks', dataIndex: 'remarks', render: (v) => v || '—' }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><HistoryOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Employee History</span>
            <span className="hr-headstrip__subtitle">Append-only change log — transfers, status, login & salesperson lifecycle.</span>
          </div>
        </div>
        <Space size={6}><span className="hr-stat-chip"><strong>{total}</strong> events</span></Space>
      </div>

      <div className="hr-toolbar">
        <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="Employee"
          value={employeeId || undefined} style={{ width: 260 }} options={employees}
          onChange={(v) => { setPage(1); setEmployeeId(v || null); }} />
        <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="Event type"
          value={eventType || undefined} style={{ width: 200 }} options={EVENT_TYPE_OPTIONS}
          onChange={(v) => { setPage(1); setEventType(v || null); }} />
        <RangePicker size="small" format="DD-MM-YYYY" value={range}
          onChange={(v) => { setPage(1); setRange(v); }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
      </div>

      <div className="hr-card">
        <Table className="hr-grid" size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns}
          scroll={{ x: 'max-content' }} sticky
          pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, onChange: setPage, size: 'small' }}
          locale={{ emptyText: 'No history events for the current filters.' }} />
      </div>
    </div>
  );
}
